import { Link, useLocation } from 'react-router-dom';
import { Shield, FileText, Info, Bell, SignOut, SignIn, Sun, Moon, Monitor, Flask, ShieldCheck, UserPlus, BookOpen, SlidersHorizontal } from '@phosphor-icons/react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme, type ThemePreference } from '../../contexts/ThemeContext';

const THEME_OPTIONS: { value: ThemePreference; icon: typeof Sun; label: string }[] = [
  { value: 'light', icon: Sun, label: 'Light' },
  { value: 'dark', icon: Moon, label: 'Dark' },
  { value: 'system', icon: Monitor, label: 'System' },
];

interface MorePopoverProps {
  isOpen: boolean;
  onClose: () => void;
  onSignInClick: () => void;
}

const NAV_LINKS = [
  { to: '/research', label: 'Research', icon: BookOpen, matchPrefix: '/research', guestOnly: false },
  { to: '/trials', label: 'Clinical Trials', icon: Flask, matchPrefix: '/trials', guestOnly: false },
  { to: '/advocacy', label: 'Advocacy', icon: Shield, matchPrefix: '/advocacy', guestOnly: false },
  { to: '/about', label: 'About', icon: Info, matchPrefix: '/about', guestOnly: false },
  { to: '/trust', label: 'Trust & Safety', icon: ShieldCheck, matchPrefix: '/trust', guestOnly: false },
];

export default function MorePopover({ isOpen, onClose, onSignInClick }: MorePopoverProps) {
  const { currentUser, logout } = useAuth();
  const { preference, setPreference } = useTheme();
  const location = useLocation();

  if (!isOpen) return null;

  const handleLogout = async () => {
    try {
      await logout();
      onClose();
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const displayName = currentUser?.displayName || currentUser?.email || 'User';

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-50 bg-black/30" onClick={onClose} />

      {/* Panel */}
      <div className="fixed bottom-16 left-4 right-4 z-50 rounded-xl bg-white dark:bg-gray-800 shadow-lg overflow-hidden"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        {/* Account header */}
        {currentUser && (
          <>
            <div className="flex items-center space-x-3 px-5 py-3.5">
              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-orange-500 to-pink-500 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                {displayName.charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{displayName.split(' ')[0]}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{currentUser.email}</p>
              </div>
            </div>
            <div className="border-t border-gray-100 dark:border-gray-700" />
          </>
        )}

        {NAV_LINKS.filter(link => {
          if (link.guestOnly && currentUser) return false;
          if ((link as any).authOnly && !currentUser) return false;
          return true;
        }).map(({ to, label, icon: Icon, matchPrefix }) => {
          const active = location.pathname.startsWith(matchPrefix);
          return (
            <Link
              key={to}
              to={to}
              onClick={onClose}
              className={`flex items-center space-x-3 px-5 py-3.5 transition-colors ${
                active
                  ? 'text-pink-700 bg-pink-50 dark:text-pink-300 dark:bg-pink-900/30 font-medium'
                  : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <Icon
                size={22}
                weight={active ? 'fill' : 'regular'}
                className={active ? 'text-pink-600 dark:text-pink-400' : 'text-gray-500 dark:text-gray-400'}
              />
              <span className="text-sm">{label}</span>
            </Link>
          );
        })}

        <div className="border-t border-gray-100 dark:border-gray-700" />

        {/* Theme selector */}
        <div className="px-5 py-3">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Theme</p>
          <div className="flex items-center gap-2">
            {THEME_OPTIONS.map(({ value, icon: Icon, label }) => (
              <button
                key={value}
                onClick={() => setPreference(value)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium transition-colors ${
                  preference === value
                    ? 'bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300'
                    : 'text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600'
                }`}
              >
                <Icon size={16} weight={preference === value ? 'fill' : 'regular'} />
                <span>{label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="border-t border-gray-100 dark:border-gray-700" />

        {currentUser ? (
          <>
            <Link
              to="/settings/your-data"
              onClick={onClose}
              className={`flex items-center space-x-3 px-5 py-3.5 transition-colors ${
                location.pathname === '/settings/your-data'
                  ? 'text-pink-700 bg-pink-50 dark:text-pink-300 dark:bg-pink-900/30 font-medium'
                  : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <FileText
                size={22}
                weight={location.pathname === '/settings/your-data' ? 'fill' : 'regular'}
                className={location.pathname === '/settings/your-data' ? 'text-pink-600 dark:text-pink-400' : 'text-gray-500 dark:text-gray-400'}
              />
              <span className="text-sm">Your Data</span>
            </Link>
            <Link
              to="/invite"
              onClick={onClose}
              className={`flex items-center space-x-3 px-5 py-3.5 transition-colors ${
                location.pathname === '/invite'
                  ? 'text-pink-700 bg-pink-50 dark:text-pink-300 dark:bg-pink-900/30 font-medium'
                  : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <UserPlus
                size={22}
                weight={location.pathname === '/invite' ? 'fill' : 'regular'}
                className={location.pathname === '/invite' ? 'text-pink-600 dark:text-pink-400' : 'text-gray-500 dark:text-gray-400'}
              />
              <span className="text-sm">Invite Friends</span>
            </Link>
            <Link
              to="/settings/notifications"
              onClick={onClose}
              className={`flex items-center space-x-3 px-5 py-3.5 transition-colors ${
                location.pathname === '/settings/notifications'
                  ? 'text-pink-700 bg-pink-50 dark:text-pink-300 dark:bg-pink-900/30 font-medium'
                  : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <Bell
                size={22}
                weight={location.pathname === '/settings/notifications' ? 'fill' : 'regular'}
                className={location.pathname === '/settings/notifications' ? 'text-pink-600 dark:text-pink-400' : 'text-gray-500 dark:text-gray-400'}
              />
              <span className="text-sm">Notification Settings</span>
            </Link>
            <Link
              to="/settings/preferences"
              onClick={onClose}
              className={`flex items-center space-x-3 px-5 py-3.5 transition-colors ${
                location.pathname === '/settings/preferences'
                  ? 'text-pink-700 bg-pink-50 dark:text-pink-300 dark:bg-pink-900/30 font-medium'
                  : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <SlidersHorizontal
                size={20}
                weight={location.pathname === '/settings/preferences' ? 'fill' : 'regular'}
                className={location.pathname === '/settings/preferences' ? 'text-pink-600 dark:text-pink-400' : 'text-gray-500 dark:text-gray-400'}
              />
              <span className="text-sm">Preferences</span>
            </Link>
            <button
              onClick={handleLogout}
              className="flex items-center space-x-3 px-5 py-3.5 w-full text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <SignOut size={22} className="text-gray-500 dark:text-gray-400" />
              <span className="text-sm">Sign Out</span>
            </button>
          </>
        ) : (
          <button
            onClick={() => {
              onClose();
              onSignInClick();
            }}
            className="flex items-center space-x-3 px-5 py-3.5 w-full text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <SignIn size={22} className="text-gray-500 dark:text-gray-400" />
            <span className="text-sm">Sign In</span>
          </button>
        )}
      </div>
    </>
  );
}
